import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import api from './lib/api';
import { jwtDecode } from 'jwt-decode';

// Components
import TestModeBanner from './components/TestModeBanner';
import { ThemeProvider } from './contexts/ThemeContext';

// Pages
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import GoalCalendar from './pages/GoalCalendar';
import HistoryPage from './pages/History';
import WeeklyTracker from './pages/WeeklyTracker';
import AuthCallback from './pages/AuthCallback';
import Upgrade from './pages/Upgrade';
import ManageSubscription from './pages/ManageSubscription';
import CreateGoal from './pages/CreateGoal';
import FAQ from './pages/FAQ';
import AdminPanel from './pages/AdminPanel';
import Help from './pages/Help';
import SharedGoals from './pages/SharedGoals';
import SharedGoalDetail from './pages/SharedGoalDetail';
import JoinSharedGoal from './pages/JoinSharedGoal';

import Landing from './pages/Landing';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refreshUser();
  }, []);

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // ideally fetch full user profile here
        const res = await api.get('/api/auth/me');
        setUser(res.data);
      } catch (e) {
        console.error("Auth error", e);
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('token', res.data.access_token);
    await refreshUser();
  };

  const register = async (email, password) => {
    const res = await api.post('/api/auth/register', { email, password });
    localStorage.setItem('token', res.data.access_token);
    await refreshUser();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen w-full flex items-center justify-center bg-background text-primary">Loading...</div>;
  if (!user) return <Navigate to="/auth" />;
  return children;
};

// Inner app component
const AppContent = () => {
  return (
    <div className="min-h-screen font-sans bg-[#FAF9F5] text-[#1A1A1A] selection:bg-[#1A1A1A] selection:text-[#FAF9F5]">
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/create-goal" element={<ProtectedRoute><CreateGoal /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><GoalCalendar /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
        <Route path="/weekly" element={<ProtectedRoute><WeeklyTracker /></ProtectedRoute>} />
        <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><ManageSubscription /></ProtectedRoute>} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/shared-goals" element={<ProtectedRoute><SharedGoals /></ProtectedRoute>} />
        <Route path="/shared-goals/:goalId" element={<ProtectedRoute><SharedGoalDetail /></ProtectedRoute>} />
        <Route path="/join/:inviteCode" element={<JoinSharedGoal />} />
        <Route path="/" element={<Landing />} />
      </Routes>
      <Toaster theme="light" position="bottom-right" />
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <TestModeBanner />
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
